import SwipeableViews from 'react-swipeable-views-react-18-fix';
import DialogContent from '@mui/material/DialogContent';
import { Stack } from '@mui/system';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {
    ArrowBackIosSharp,
    ArrowForwardIosSharp,
    Circle
} from '@mui/icons-material';
import { DialogActions, styled, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { CloseButton } from '../buttons/CloseButton';
import { useTheme } from '@mui/material/styles';
import { GoProButton } from '../buttons/GoProButton';
import { OverTimeChartShowcase } from './OverTimeChartShowcase';
import { SkillsBarChartShowcase } from './SkillsBarChartShowcase';
import { PricingShowcase } from './PricingShowcase';
import { InsightLogo } from '../logos/InsightLogo';
import DialogTitle from '@mui/material/DialogTitle';

//styled for layout with preview page in between arrows
const ArrowButton = styled(Button)(() => ({
    position: 'absolute',
    minWidth: 0,
    minHeight: 80,
    width: '15%',
    padding: 0,
    top: '40%'
}));

const PreviewHeadingText = styled(Typography)(({ theme }) => ({
    color: theme.palette.font.white,
    textAlign: 'center',
    fontWeight: 700,
    whiteSpace: 'pre-wrap',
    fontSize: '1.65rem',
    lineHeight: '1.8rem'
}));

const PreviewDescriptionText = styled(Typography)(({ theme }) => ({
    color: theme.palette.font.white,
    textAlign: 'center'
}));

//Add any new preview pages for this details dialog into this array.
const proPreviewPageConfig = [
    {
        id: 'skills',
        heading: 'Drill down to specific decision skills',
        ShowcaseComponent: SkillsBarChartShowcase,
        description:
            'Unlock insight into which types of decision are adding and destroying value'
    },
    {
        id: 'overTime',
        heading: 'Peer comparison\nover time',
        ShowcaseComponent: OverTimeChartShowcase,
        description:
            "Compare the portfolio's Behavioral Alpha Score with those of the relevant peer group, over time"
    },
    {
        id: 'pricing',
        heading: 'Pricing',
        ShowcaseComponent: PricingShowcase
    }
];

/**
 * Component for displaying an Insight Pro preview page with a heading, main component, and description.
 * @component
 * @param {Object} props - The properties passed to the component
 * @param {string} props.id - The preview page identifier
 * @param {string} props.heading - The preview page heading text
 * @param {string} props.ShowcaseComponent - Showcase page component
 * @param {string} props.description - The preview page description text
 * @returns {JSX.Element}
 */
const InsightProPreviewPage = ({
    id,
    heading,
    ShowcaseComponent,
    description
}) => {
    return (
        <Stack id={id} spacing={3} alignItems='center' sx={{ width: 274 }}>
            <PreviewHeadingText>{heading}</PreviewHeadingText>
            <ShowcaseComponent />
            <PreviewDescriptionText variant='body2'>
                {description}
            </PreviewDescriptionText>
        </Stack>
    );
};

InsightProPreviewPage.propTypes = {
    id: PropTypes.string.isRequired,
    heading: PropTypes.string,
    ShowcaseComponent: PropTypes.elementType,
    description: PropTypes.string
};

/**
 * Component for displaying Insight Pro features, showcasing them through
 * a series of preview pages that can be cycled through using left and right arrows.
 * Each view displays an image and description for a particular Pro feature.
 * Also present is a button allowing the user to open a form to submit
 * a GoPro user action, and a close button to dismiss the dialog.
 *
 * @component
 * @param {Object} props - The properties passed to the component
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Function} props.onGoProClick - Function called when Go Pro button clicked
 */
export const InsightProDetails = ({ onClose, onGoProClick }) => {
    const theme = useTheme();

    const [proDetailsPageIndex, setProDetailsPageIndex] = useState(0);

    /**
     * Change currently displayed preview page to the subsequent
     * item in the array, or to the first item, if at the end of the array
     */
    const handleForwardArrowClick = () => {
        if (proDetailsPageIndex === proPreviewPageConfig.length - 1) {
            setProDetailsPageIndex(0);
        } else {
            setProDetailsPageIndex(proDetailsPageIndex + 1);
        }
    };

    /**
     * Change currently displayed preview page to the previous
     * item in the array, or to the last item, if at the start of the array
     */
    const handleBackwardArrowClick = () => {
        if (proDetailsPageIndex === 0) {
            setProDetailsPageIndex(proPreviewPageConfig.length - 1);
        } else {
            setProDetailsPageIndex(proDetailsPageIndex - 1);
        }
    };

    return (
        <>
            <DialogTitle sx={{ p: 3, pb: 2, pr: 2 }}>
                <Stack direction='row' justifyContent='space-between'>
                    <InsightLogo
                        fontSize={25}
                        color={theme.palette.common.white}
                        subType='pro'
                    />
                    <CloseButton
                        color={theme.palette.common.white}
                        onClose={onClose}
                    />
                </Stack>
            </DialogTitle>
            <DialogContent
                sx={{
                    padding: 3,
                    overflowY: 'visible' //avoid scrollbar showing unnecessarily
                }}
            >
                <Stack
                    spacing={2}
                    sx={{ height: '100%', justifyContent: 'center' }}
                >
                    {/*arrows and current preview page */}
                    <SwipeableViews
                        axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                        index={proDetailsPageIndex}
                        onChangeIndex={(index) => setProDetailsPageIndex(index)}
                    >
                        {proPreviewPageConfig.map((page) => (
                            <Box
                                key={page.id}
                                sx={{
                                    height: 430,
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                <InsightProPreviewPage
                                    key={page.id}
                                    {...page}
                                />
                            </Box>
                        ))}
                    </SwipeableViews>
                    <ArrowButton
                        sx={{ left: 5 }}
                        onClick={handleBackwardArrowClick}
                        data-analyticsid='backward-arrow'
                    >
                        <ArrowBackIosSharp />
                    </ArrowButton>
                    <ArrowButton
                        sx={{ right: 5 }}
                        onClick={handleForwardArrowClick}
                        data-analyticsid='forward-arrow'
                    >
                        <ArrowForwardIosSharp />
                    </ArrowButton>
                    {/*preview page dots*/}
                    <Stack direction='row' justifyContent='center'>
                        {proPreviewPageConfig.map((item, index) => (
                            <Circle
                                key={item.id}
                                style={{
                                    height: 10,
                                    color: theme.palette.common.white,
                                    opacity:
                                        index === proDetailsPageIndex ? 1 : 0.6
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ padding: 3, paddingTop: 1 }}>
                <GoProButton
                    onClick={onGoProClick}
                    sxProps={{ height: 40, lineHeight: 1 }}
                />
            </DialogActions>
        </>
    );
};

InsightProDetails.propTypes = {
    onClose: PropTypes.func.isRequired,
    onGoProClick: PropTypes.func.isRequired
};
